import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '@shared/ui/LoadingSpinner';

/**
 * CalendarShortUrlPage - Handles short URL redirects for public calendar
 * 
 * Short URL patterns:
 *   /c/a/:date        → /public/calendar/all/:date
 *   /c/o/:orgId/:date → /public/calendar/org/:orgId/:date
 *   /c/w/:wsId/:date  → /public/calendar/ws/:wsId/:date
 *   /c/a/w/:startDate        → /public/calendar/all/week/:startDate
 *   /c/o/:orgId/w/:startDate → /public/calendar/org/:orgId/week/:startDate
 *   /c/w/:wsId/w/:startDate  → /public/calendar/ws/:wsId/week/:startDate
 *   /c/a/m/:startDate        → /public/calendar/all/month/:startDate
 *   /c/o/:orgId/m/:startDate → /public/calendar/org/:orgId/month/:startDate
 *   /c/w/:wsId/m/:startDate  → /public/calendar/ws/:wsId/month/:startDate
 *   /c/e/:eventId     → /public/calendar/events/:eventId
 * 
 * This is a client-side redirect - no API call needed since the URLs are deterministic
 */

const CalendarShortUrlPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Parse the path segments from the URL
  const pathParts = location.pathname.split('/').filter(Boolean);
  // pathParts example: ['c', 'a', '2026-01-08'] or ['c', 'o', 'uuid', '2026-01-08']

  useEffect(() => {
    if (pathParts.length < 3) {
      // Invalid short URL format, redirect to homepage
      navigate('/', { replace: true });
      return;
    }

    const [, urlScope, ...rest] = pathParts;
    let redirectPath = '/';

    try {
      if (urlScope === 'a') {
        // All events
        // /c/a/:date        → /calendar/:date (authenticated) or /public/calendar/all/:date (public)
        // /c/a/w/:startDate → /public/calendar/all/week/:startDate
        // /c/a/m/:startDate → /public/calendar/all/month/:startDate
        const maybeRange = rest[0];
        const maybeDate = rest[1] || rest[0];

        if (maybeRange === 'w' && maybeDate && isValidDateFormat(maybeDate)) {
          redirectPath = `/public/calendar/all/week/${maybeDate}`;
        } else if (maybeRange === 'm' && maybeDate && isValidDateFormat(maybeDate)) {
          redirectPath = `/public/calendar/all/month/${maybeDate}`;
        } else if (rest[0] && isValidDateFormat(rest[0])) {
          // Auth-aware: logged-in users go to the authenticated calendar detail page
          const token = localStorage.getItem('token');
          redirectPath = token
            ? `/calendar/${rest[0]}`
            : `/public/calendar/all/${rest[0]}`;
        }
      } else if (urlScope === 'o') {
        // Organization
        // /c/o/:orgId/:date        → /public/calendar/org/:orgId/:date
        // /c/o/:orgId/w/:startDate → /public/calendar/org/:orgId/week/:startDate
        // /c/o/:orgId/m/:startDate → /public/calendar/org/:orgId/month/:startDate
        const orgId = rest[0];
        const maybeRange = rest[1];
        const maybeDate = rest[2] || rest[1];

        if (orgId && maybeRange === 'w' && maybeDate && isValidDateFormat(maybeDate)) {
          redirectPath = `/public/calendar/org/${orgId}/week/${maybeDate}`;
        } else if (orgId && maybeRange === 'm' && maybeDate && isValidDateFormat(maybeDate)) {
          redirectPath = `/public/calendar/org/${orgId}/month/${maybeDate}`;
        } else {
          const date = rest[1];
          if (orgId && date && isValidDateFormat(date)) {
            redirectPath = `/public/calendar/org/${orgId}/${date}`;
          }
        }
      } else if (urlScope === 'w') {
        // Workspace
        // /c/w/:wsId/:date        → /public/calendar/ws/:wsId/:date
        // /c/w/:wsId/w/:startDate → /public/calendar/ws/:wsId/week/:startDate
        // /c/w/:wsId/m/:startDate → /public/calendar/ws/:wsId/month/:startDate
        const wsId = rest[0];
        const maybeRange = rest[1];
        const maybeDate = rest[2] || rest[1];

        if (wsId && maybeRange === 'w' && maybeDate && isValidDateFormat(maybeDate)) {
          redirectPath = `/public/calendar/ws/${wsId}/week/${maybeDate}`;
        } else if (wsId && maybeRange === 'm' && maybeDate && isValidDateFormat(maybeDate)) {
          redirectPath = `/public/calendar/ws/${wsId}/month/${maybeDate}`;
        } else {
          const date = rest[1];
          if (wsId && date && isValidDateFormat(date)) {
            redirectPath = `/public/calendar/ws/${wsId}/${date}`;
          }
        }
      } else if (urlScope === 'e') {
        // Single event: /c/e/:eventId → /public/calendar/events/:eventId
        const eventId = rest[0];
        if (eventId && /^\d+$/.test(eventId)) {
          redirectPath = `/public/calendar/events/${eventId}`;
        }
      }
    } catch (e) {
      console.error('Error parsing short URL:', e);
    }

    navigate(redirectPath, { replace: true });
  }, [location.pathname, navigate, pathParts]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark-primary">
      <div className="text-center">
        <LoadingSpinner size="xl" className="mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
          Loading Calendar...
        </h2>
        <p className="text-text-secondary dark:text-text-dark-secondary">
          Redirecting you to the calendar page
        </p>
      </div>
    </div>
  );
};

/**
 * Validate date format (YYYY-MM-DD)
 */
function isValidDateFormat(date: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(date);
}

export default CalendarShortUrlPage;

